import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse, PaginatedItemsApiResponse } from '../models/api-response.model';
import { hasMorePages, paginatedItems } from '../utils/paginated.util';
import {
  StaffType,
  StaffTypeFilterRequest,
  StaffTypePayload,
  StaffTypeUpdatePayload,
} from '../models/staff-type.model';

@Injectable({
  providedIn: 'root',
})
export class StaffTypeService {
  private readonly http = inject(HttpClient);
  private readonly endpoint = `${environment.apiUrl}/v1/staff-type`;

  public create(request: StaffTypePayload): Observable<ApiResponse<StaffType>> {
    return this.http.post<ApiResponse<StaffType>>(this.endpoint, request);
  }

  public getById(id: string): Observable<ApiResponse<StaffType>> {
    return this.http.get<ApiResponse<StaffType>>(`${this.endpoint}/${id}`);
  }

  public update(id: string, request: StaffTypeUpdatePayload): Observable<ApiResponse<StaffType>> {
    return this.http.patch<ApiResponse<StaffType>>(`${this.endpoint}/${id}`, request);
  }

  public delete(id: string): Observable<ApiResponse<StaffType>> {
    return this.http.delete<ApiResponse<StaffType>>(`${this.endpoint}/${id}`);
  }

  public filterPaginated(
    request: StaffTypeFilterRequest,
  ): Observable<PaginatedItemsApiResponse<StaffType>> {
    return this.http.post<PaginatedItemsApiResponse<StaffType>>(
      `${this.endpoint}/filter-paginated`,
      request,
    );
  }

  /**
   * Every staff type matching the filter, following pagination to the last page.
   *
   * The role column, the role picker and the gestor detail all resolve names from this
   * list, so a single page would leave anything past it unnamed or unselectable.
   * `pagination.pageSize` sets the page size used while walking; `pageNumber` is ignored.
   */
  public getAllFilteredStaffTypes(request: StaffTypeFilterRequest): Observable<StaffType[]> {
    const page = (n: number) =>
      this.filterPaginated({ ...request, pagination: { ...request.pagination, pageNumber: n } });
    return page(1).pipe(
      expand((res) => (hasMorePages(res) ? page(res.data.meta.page + 1) : EMPTY)),
      reduce((acc, res) => acc.concat(paginatedItems(res)), [] as StaffType[]),
    );
  }
}
